import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Heading } from '@clay-run/glaze/heading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@clay-run/glaze/table';
import { Text } from '@clay-run/glaze/text';
import { useAppBasePath } from '../../_shared/base-path';
import { useAdRequests } from '../api-client';
import { usd } from '../format';
import { PLATFORM_LABELS, type AdRequestStatus } from '../types';
import { RequestStatusBadge } from './StatusBadge';

const STATUS_ORDER: Record<AdRequestStatus, number> = {
  needs_review: 0,
  publishing: 1,
  changes_requested: 2,
  published: 3,
  rejected: 4,
};

export default function ReviewQueuePage() {
  const base = useAppBasePath();
  const { data: requests, isLoading, isError } = useAdRequests();

  const rows = useMemo(
    () =>
      [...(requests ?? [])].sort(
        (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || b.submittedAt.localeCompare(a.submittedAt),
      ),
    [requests],
  );
  const waiting = rows.filter(r => r.status === 'needs_review').length;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <Heading level={1} size="lg">
          Review queue
        </Heading>
        {requests && (
          <Text size="sm" variant="tertiary">
            {waiting} waiting for review
          </Text>
        )}
      </div>
      {isLoading && <Text variant="tertiary">Loading requests...</Text>}
      {isError && <Text variant="tertiary">Requests couldn't be loaded.</Text>}
      {requests?.length === 0 && (
        <Text variant="tertiary">
          No requests yet.{' '}
          <Link to={`${base}/new`} className="underline">
            Create one
          </Link>
          .
        </Text>
      )}
      {rows.length > 0 && (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Request</TableHeader>
              <TableHeader>Team</TableHeader>
              <TableHeader>Platforms</TableHeader>
              <TableHeader>Audience</TableHeader>
              <TableHeader align="right">Budget / day</TableHeader>
              <TableHeader>Submitted</TableHeader>
              <TableHeader>Status</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(request => (
              <TableRow key={request.id}>
                <TableCell>
                  <div className="flex flex-col gap-0.5">
                    <Link to={`${base}/requests/${request.id}`} className="font-medium hover:underline">
                      {request.name}
                    </Link>
                    {request.creativeCount > 1 && (
                      <Text size="xs" variant="tertiary">
                        {request.creativeCount} creatives
                      </Text>
                    )}
                    {request.reviewFlag && (
                      <Text size="xs" variant="warning">
                        {request.reviewFlag}
                      </Text>
                    )}
                  </div>
                </TableCell>
                <TableCell>{request.team || '—'}</TableCell>
                <TableCell>{request.platforms.map(p => PLATFORM_LABELS[p]).join(', ')}</TableCell>
                <TableCell>{request.audienceName ?? 'US + Canada, 28–60'}</TableCell>
                <TableCell align="right">{usd(request.dailyBudgetUsd)}</TableCell>
                <TableCell>
                  <div className="flex flex-col gap-0.5">
                    <span>{new Date(request.submittedAt).toLocaleDateString()}</span>
                    {request.requesterEmail && (
                      <span className="text-xs text-content-tertiary">{request.requesterEmail.split('@')[0]}</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <RequestStatusBadge status={request.status} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}
